<template>
    <highcharts class="hc" :ref="refId" :options="options"></highcharts>
</template>

<script>
    // utils
    import {getKey} from "../../utils/common";
    // constants
    import {chartColors} from "../../constants/chart-constants";
    // external
    import Highcharts from 'highcharts';
    import exportingInit from 'highcharts/modules/exporting';
    import exportingData from 'highcharts/modules/export-data';
    // mixins
    import computePlotLinesMixin from "../../mixins/computePlotLinesMixin";
    import ChartZoomControlMixin from "../../mixins/ChartZoomControlMixin";
    import ChartOptionsMixin from "../../mixins/ChartOptionsMixin";

    Highcharts.setOptions({
        colors: chartColors
    });

    exportingInit(Highcharts);
    exportingData(Highcharts);
    /**
     * Wrapper for highcharts component.
     * Has dynamic ref key and zoom reset handler.
     * Initializes localization for the chart and
     * holds the options for reverse SPLINE type charts.
     */

    export default {
        name: "SplineReverseChart",
        mixins: [computePlotLinesMixin, ChartZoomControlMixin, ChartOptionsMixin],
        props: {
            zoomKey: {
                type: Number,
                required: true
            },
            data: {
                type: Object,
                required: true
            },
            metric: {
                type: String
            },
            showPlotLine: {
                type: Boolean
            }
        },
        created() {
            Highcharts.setOptions({
                lang: this.getChartTranslations()
            });
        },
        computed: {
            /**
             * Get a dynamic key for referencing the highcharts object.
             * NOTE: zoom level is controlled viz the zoomKey and this.
             */
            refId() {
                return getKey();
            },
            /**
             * Gets the indexes of the first data set and use
             * them as custom ticks (the only ticks).
             * @returns {*[]}
             */
            ticksFromIndexes() {
                let ticks = [];
                if (this.data.series.length > 0) {
                    const initSeries = this.data.series[0];
                    for (let idx = 0; idx < initSeries.data.length; idx++) {
                        ticks.push(initSeries.data[idx][0]);
                    }
                }
                return ticks;
            },
            options() {
                let options = {
                    chart: {
                        zoomType: 'y',
                        type: 'spline',
                        inverted: true,
                        displayErrors: false,
                        height: 800
                    },
                    exporting: this.getChartExportingOptions(),
                    resetZoomButton: {
                        theme: {
                            display: 'none'
                        }
                    },
                    title: {
                        text: null
                    },
                    xAxis: {
                        reversed: false,
                        title: {
                            // reversed!
                            text: this.$i18n.t('sensors-page.inclinometers.y-label'),
                            style: {
                                color: "#6299EB"
                            }
                        },
                        labels: {
                            x: -10,
                            y: -1
                        },
                        tickPositions: this.ticksFromIndexes
                    },
                    yAxis: {
                        title: {
                            text: this.$i18n.t(this.metric || 'sensors-page.inclinometers.x-label'),
                            style: {
                                color: "#6299EB"
                            }
                        },
                        labels: {
                            x: 0,
                            y: -2
                        },
                        softMin: -15,
                        softMax: 15
                    },
                    legend: {
                        enabled: true
                    },
                    credits: {
                        enabled: false
                    },
                    plotOptions: {
                        areaspline: {
                            fillOpacity: 0,
                            marker: {
                                radius: 3
                            },
                            lineWidth: 1,
                            states: {
                                hover: {
                                    lineWidth: 1
                                }
                            },
                            threshold: null
                        }
                    }
                };

                // add data
                options.series = this.data.series;
                // set min and max dynamically  // TODO
                // add possible plot lines
                if (this.showPlotLine) {
                    options.xAxis.plotLines = this.computePlotLines(this.data.plotLines.xAxis);
                    options.yAxis.plotLines = this.computePlotLines(this.data.plotLines.yAxis);
                }

                return options;
            }
        }
    };
</script>
